import React, { useEffect, useState, useRef } from 'react';
import { useAuthStore } from '../lib/auth';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient'; // REAL Supabase client restored
import { phase2API } from '../lib/api';
import {
  Shield,
  FileText,
  BookOpen,
  AlertTriangle,
  Building2,
  Users,
  CheckCircle2,
  Clock,
  TrendingUp,
  ChevronRight
} from 'lucide-react';
// FIX 2: Restored your real shadcn components to bring back your dark mode theme naturally
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const ComplianceHubPage = () => {
  const { user } = useAuthStore();
  const [complianceScore, setComplianceScore] = useState(100);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({ verified: 0, pending: 0, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const searchDebounce = useRef(null);

  useEffect(() => {
    fetchRealTimeCompliance();
  }, []);

  const fetchRealTimeCompliance = async () => {
    try {
      const { data, error } = await supabase
        .from('compliance_documents')
        .select('status');

      if (error) throw error;

      let pendingCount = 0;
      let rejectedCount = 0;
      let verifiedCount = 0;

      data.forEach(doc => {
        if (doc.status === 'pending_review') pendingCount++;
        if (doc.status === 'rejected') rejectedCount++;
        if (doc.status === 'verified') verifiedCount++;
      });

      const totalDocs = data.length;
      
      if (totalDocs > 0) {
        const score = Math.round(((verifiedCount + pendingCount) / totalDocs) * 100);
        setComplianceScore(Math.max(0, score - (rejectedCount * 5))); 
      }

      setStats({ verified: verifiedCount, pending: pendingCount, total: totalDocs });

      const liveAlerts = [];
      if (rejectedCount > 0) {
        liveAlerts.push({ 
          message: `${rejectedCount} document(s) failed verification. Immediate re-upload required.`, 
          severity: 'high' 
        });
      }
      if (pendingCount > 0) {
        liveAlerts.push({ 
          message: `${pendingCount} document(s) in the vault pending EHO/Admin review.`, 
          severity: 'medium' 
        });
      }

      setAlerts(liveAlerts);

    } catch (err) {
      console.error('Failed to fetch compliance data:', err);
    }
  };

  useEffect(() => {
    if (searchDebounce.current) {
      clearTimeout(searchDebounce.current);
    }

    if (!searchTerm || searchTerm.trim().length < 2) {
      setSearchResults([]);
      setSearchError(null);
      return;
    }

    setIsSearching(true);
    searchDebounce.current = window.setTimeout(async () => {
      try {
        const { data } = await phase2API.searchCoshhSds(searchTerm.trim());
        setSearchResults(data || []);
        setSearchError(null);
      } catch (error) {
        console.error('COSHH search failed:', error);
        setSearchResults([]);
        setSearchError('Unable to complete search. Please try again.');
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      if (searchDebounce.current) {
        clearTimeout(searchDebounce.current);
      }
    };
  }, [searchTerm]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const modules = [
    {
      title: "Document Verification",
      description: "SOP/SSOW active reading with digital sign-off",
      icon: FileText,
      path: "/compliance/documents",
      color: "text-blue-500",
      bgColor: "bg-blue-50 dark:bg-blue-950"
    },
    {
      title: "Knowledge Proofing",
      description: "Spot-check questions during audits",
      icon: BookOpen,
      path: "/compliance/knowledge",
      color: "text-green-500",
      bgColor: "bg-green-50 dark:bg-green-950"
    },
    {
      title: "Regulatory Modules",
      description: "Allergen matrix, COSHH, certifications",
      icon: Shield,
      path: "/compliance/regulatory",
      color: "text-purple-500",
      bgColor: "bg-purple-50 dark:bg-purple-950"
    },
    {
      title: "Enterprise Mapping",
      description: "Multi-site hierarchy & regional management",
      icon: Building2,
      path: "/compliance/enterprise",
      color: "text-orange-500",
      bgColor: "bg-orange-50 dark:bg-orange-950"
    },
    {
      title: "EHO Safeguards",
      description: "Pre-configured audits & anti-fraud measures",
      icon: AlertTriangle,
      path: "/compliance/eho",
      color: "text-red-500",
      bgColor: "bg-red-50 dark:bg-red-950"
    },
    {
      title: "Staff & HR Hub",
      description: "Training expiry & staff requests",
      icon: Users,
      path: "/compliance/staff",
      color: "text-indigo-500",
      bgColor: "bg-indigo-50 dark:bg-indigo-950"
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#FF6600]">EHO-Shield Compliance</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Real-time compliance engine & paper trail verification.
          </p>
        </div>
        <div className="text-right">
          <div className={`text-3xl font-bold ${complianceScore >= 90 ? 'text-[#00E676]' : complianceScore >= 70 ? 'text-[#FFB300]' : 'text-[#FF3B30]'}`}>
            {complianceScore}%
          </div>
          <div className="text-sm font-bold uppercase text-[#A1A1AA]">Health Score</div>
        </div>
      </div>

      {alerts.length > 0 && (
        <Card className="border-[#3F3F46] bg-[#1E1E1E]">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-white text-lg">
              <AlertTriangle className="w-5 h-5 text-[#FF3B30]" />
              Active Action Items
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {alerts.map((alert, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-[#27272A] border border-[#3F3F46] rounded-lg">
                  <span className="text-sm text-white font-medium">{alert.message}</span>
                  <Badge className={alert.severity === 'high' ? 'bg-[#FF3B30]/20 text-[#FF3B30] hover:bg-[#FF3B30]/30 border-transparent' : 'bg-[#FFB300]/20 text-[#FFB300] hover:bg-[#FFB300]/30 border-transparent'}>
                    {alert.severity.toUpperCase()}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {modules.map((module) => {
          const Icon = module.icon;
          return (
            <Link key={module.path} to={module.path}>
              <Card className="hover:border-[#FF6600] transition-colors cursor-pointer group bg-[#1E1E1E] border-[#3F3F46] h-full">
                <CardHeader>
                  <div className={`w-12 h-12 rounded-lg ${module.bgColor} flex items-center justify-center mb-3 group-hover:scale-105 transition-transform`}>
                    <Icon className={`w-6 h-6 ${module.color}`} />
                  </div>
                  <CardTitle className="flex items-center justify-between text-white">
                    {module.title}
                    <ChevronRight className="w-4 h-4 text-[#A1A1AA] group-hover:text-[#FF6600] transition-colors" />
                  </CardTitle>
                  <CardDescription className="text-[#A1A1AA] mt-2">{module.description}</CardDescription>
                </CardHeader>
              </Card>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-[#1E1E1E] border-[#3F3F46]">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-[#00E676]" />
              <div>
                <div className="text-2xl font-bold text-white">{stats.verified}</div>
                <div className="text-sm font-bold uppercase text-[#A1A1AA]">Verified Docs</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#1E1E1E] border-[#3F3F46]">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-[#FFB300]" />
              <div>
                <div className="text-2xl font-bold text-white">{stats.pending}</div>
                <div className="text-sm font-bold uppercase text-[#A1A1AA]">Pending Review</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#1E1E1E] border-[#3F3F46]">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-[#FF6600]" />
              <div>
                <div className="text-2xl font-bold text-white">{stats.total}</div>
                <div className="text-sm font-bold uppercase text-[#A1A1AA]">Total Vault Size</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#1E1E1E] border-[#3F3F46]">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-500" />
              <div>
                <div className="text-2xl font-bold text-white">Active</div>
                <div className="text-sm font-bold uppercase text-[#A1A1AA]">EHO Shield</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ComplianceHubPage;